import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { AuthenticationService } from '../authentication/authentication.service';
import { catchError, finalize, map, of } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Store } from '@ngrx/store';
import { seUserLogout } from '../../root-store/root.actions';

export interface IdleLoginDialogResult {
    domain: string;
    username: string;
    password: string;
}

@Component({
    selector: 'se-idle-dialog',
    standalone: true,
    template: `
        <form (submit)="onLogin()">
            <mat-dialog-content class="mat-typography">
                <p translate>IdleTimeoutWarningExpired</p>

                <mat-form-field>
                    <mat-label translate>Domain</mat-label>
                    <input matInput disabled placeholder="{{ 'Domain' | translate }}" [value]="loginData.domain" />
                </mat-form-field>

                <mat-form-field>
                    <mat-label translate>Username</mat-label>
                    <input matInput disabled placeholder="{{ 'Username' | translate }}" [value]="loginData.username" />
                </mat-form-field>

                <mat-form-field>
                    <mat-label translate>Password</mat-label>
                    <input
                        type="{{ showPassword ? 'text' : 'password' }}"
                        matInput
                        cdkFocusInitial
                        placeholder="{{ 'Password' | translate }}"
                        name="password"
                        [(ngModel)]="loginData.password"
                    />
                    <button
                        type="button"
                        matSuffix
                        mat-icon-button
                        aria-label="Show"
                        (click)="showPassword = !showPassword"
                        tabindex="-1"
                    >
                        <mat-icon>{{ showPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
                    </button>
                </mat-form-field>

                <mat-error translate>{{ error ?? '&nbsp;' }}</mat-error>
            </mat-dialog-content>

            <mat-dialog-actions align="end">
                <mat-spinner *ngIf="isLoggingIn" diameter="20"></mat-spinner>

                <button mat-button type="submit" color="primary" [disabled]="isLoggingIn">
                    <span translate>Login</span>
                </button>

                <button mat-button type="button" [disabled]="isLoggingIn" (click)="onLogout()">
                    <span translate>Logout</span>
                </button>
            </mat-dialog-actions>
        </form>
    `,
    styles: [
        `
            mat-dialog-content {
                flex-direction: column;
                display: flex;
                gap: 0.5em;
            }
            mat-error {
                margin-top: 1em;
            }
        `,
    ],
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
    ],
})
export class IdleLoginDialogComponent implements OnInit {
    readonly loginData: IdleLoginDialogResult = {
        domain: '',
        username: '',
        password: '',
    };

    error = '';
    isLoggingIn = false;
    showPassword = false;

    constructor(
        private authentication: AuthenticationService,
        private dialogRef: MatDialogRef<IdleLoginDialogResult>,
        private store: Store,
    ) {}

    async ngOnInit() {
        const userInfo = await this.authentication.getUserInfo();
        this.loginData.domain = userInfo.domain ?? '';
        this.loginData.username = userInfo.userName ?? '';
    }

    onLogin() {
        this.isLoggingIn = true;
        this.error = '';

        this.authentication
            .login({
                domain: this.loginData.domain,
                userName: this.loginData.username,
                password: this.loginData.password,
            })
            .pipe(
                map(() => undefined),
                catchError(() => of('WrongUserNameOrPassword')),
                finalize(() => (this.isLoggingIn = false)),
            )
            .subscribe((err) => {
                if (err) {
                    this.error = err;
                } else {
                    this.dialogRef.close();
                }
            });
    }

    onLogout() {
        this.store.dispatch(seUserLogout({}));
    }
}
